// src/contexts/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { message } from 'antd';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

// 模拟用户数据
const MOCK_USERS = [
  {
    id: 1,
    username: '叽里咕噜',
    email: 'jiligulu@gmail.com',
    avatarUrl: '/images/叽里咕噜.image.jpg',
    bio: '这个人很神秘，什么也没留下。',
    detailedIntro: '欢迎来到我的博客！请登陆后完善您的个人信息！',
    techStack: ['React', 'Node.js', 'TypeScript'],
    hobbies: '编程、阅读、旅行',
    createdAt: '2024-01-01'
  },
  {
    id: 2,
    username: '王五',
    email: 'wang@gmail.com',
    avatarUrl: '',
    bio: '这个人很神秘，什么也没留下。',
    detailedIntro: '欢迎来到我的博客！请登陆后完善您的个人信息！',
    techStack: ['Vue', 'Python'],
    hobbies: '摄影、音乐',
    createdAt: '2024-02-01'
  }
];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = () => {
      try {
        const savedUser = localStorage.getItem('blog_user');
        if (savedUser) {
          const userData = JSON.parse(savedUser);
          // 确保techStack是数组
          if (userData.techStack && !Array.isArray(userData.techStack)) {
            if (typeof userData.techStack === 'string') {
              userData.techStack = userData.techStack.split(',').map(item => item.trim()).filter(Boolean);
            } else {
              userData.techStack = [];
            }
          }
          setUser(userData);
        }
      } catch (error) {
        console.error('读取用户信息失败:', error);
        localStorage.removeItem('blog_user');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // 创建新用户时确保techStack是数组
  const createNewUser = (email) => {
    return {
      id: Date.now(),
      username: email.split('@')[0],
      email: email,
      avatarUrl: '',
      bio: '这个人很神秘，什么也没留下。',
      detailedIntro: '欢迎来到我的博客！请登陆后完善您的个人信息！',
      techStack: [],
      hobbies: '暂无',
      createdAt: new Date().toISOString().split('T')[0]
    };
  };

  const login = async (email, password) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const foundUser = MOCK_USERS.find(user => user.email === email);
      
      if (foundUser) {
        const userData = { ...foundUser };
        setUser(userData);
        localStorage.setItem('blog_user', JSON.stringify(userData));
        localStorage.setItem('blog_token', 'mock-jwt-token-' + Date.now());
        message.success(`欢迎回来，${foundUser.username}！`);
        return { success: true, user: userData };
      } else {
        const newUser = createNewUser(email);
        
        setUser(newUser);
        localStorage.setItem('blog_user', JSON.stringify(newUser));
        localStorage.setItem('blog_token', 'mock-jwt-token-' + Date.now());
        message.success('注册成功！欢迎使用博客系统');
        return { success: true, user: newUser };
      }
    } catch (error) {
      message.error('登录失败，请重试');
      return { success: false, error: error.message };
    }
  };

  // 退出登录
  const logout = () => {
    setUser(null);
    localStorage.removeItem('blog_user');
    localStorage.removeItem('blog_token');
    message.success('已退出登录');
  };

  // 更新用户信息时确保techStack是数组
  const updateUser = (userData) => {
    const updatedUser = { 
      ...user, 
      ...userData 
    };
    
    // 如果更新了techStack，确保它是数组
    if (userData.techStack !== undefined) {
      if (Array.isArray(userData.techStack)) {
        updatedUser.techStack = userData.techStack;
      } else if (typeof userData.techStack === 'string') {
        updatedUser.techStack = userData.techStack.split(',').map(item => item.trim()).filter(Boolean);
      } else {
        updatedUser.techStack = [];
      }
    }
    
    setUser(updatedUser);
    localStorage.setItem('blog_user', JSON.stringify(updatedUser));
    message.success('个人信息已更新');
    return updatedUser;
  };

  // 添加技术栈
  const addTechStack = (tech) => {
    if (tech && user) {
      const currentTechStack = Array.isArray(user.techStack) 
        ? user.techStack 
        : (typeof user.techStack === 'string' 
            ? user.techStack.split(',').map(item => item.trim()).filter(Boolean) 
            : []);
      
      if (!currentTechStack.includes(tech)) {
        const updatedUser = { 
          ...user, 
          techStack: [...currentTechStack, tech] 
        };
        setUser(updatedUser);
        localStorage.setItem('blog_user', JSON.stringify(updatedUser));
      }
    }
  };

  // 删除技术栈 - 修复了这里的错误
  const removeTechStack = (index) => {
    if (user && user.techStack) {
      const currentTechStack = Array.isArray(user.techStack) 
        ? [...user.techStack] 
        : (typeof user.techStack === 'string' 
            ? user.techStack.split(',').map(item => item.trim()).filter(Boolean) // 这里修复为 user.techStack
            : []);
      
      if (index >= 0 && index < currentTechStack.length) {
        currentTechStack.splice(index, 1);
        const updatedUser = { ...user, techStack: currentTechStack };
        setUser(updatedUser);
        localStorage.setItem('blog_user', JSON.stringify(updatedUser));
      }
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isLoading,
      login, 
      logout, 
      updateUser,
      addTechStack,
      removeTechStack,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};